import glFetch from 'logic/rest/FetchProvider';
import { qualifyUrl } from 'util/URLUtils';
import EnterpriseApiRoutes from 'common/ApiRoutes';
import type SingletonParameter from 'views/logic/parameters/Parameter';
import type { EntityShare } from 'actions/permissions/EntityShareActions';

import type {
  EventProceduresIndexAPIType,
  EventProceduresAPIType,
  EventProcedureStepsIndexAPIType,
  EventProcedureStepAPIType,
} from './eventProceduresAPI.types';

export const fetchEventProcedures = async (
  page: number,
  perPage: number,
  query: string = null,
  orderBy: string = undefined,
  direction: string = undefined,
): Promise<EventProceduresIndexAPIType> => {
  const params = [`page=${page}`, `per_page=${perPage}`];
  if (query) params.push(`query=${encodeURI(query)}`);

  if (orderBy) {
    params.push(`sort=${orderBy}`);
  }

  if (direction) {
    params.push(`direction=${direction}`);
  }

  return glFetch('GET', qualifyUrl(EnterpriseApiRoutes.SecurityApp.EventProcedures.index(params.join('&')).url));
};

export const fetchEventProcedure = async (
  eventProcedureId: string,
): Promise<EventProceduresAPIType<EventProcedureStepAPIType[]>> =>
  glFetch(
    'GET',
    qualifyUrl(EnterpriseApiRoutes.SecurityApp.EventProcedures.singleEventProcedure(eventProcedureId).url),
  );

export const fetchDecoratedEventProcedure = async (
  eventProcedureId: string,
  eventId: string,
): Promise<EventProceduresAPIType<EventProcedureStepAPIType[]>> =>
  glFetch(
    'GET',
    qualifyUrl(EnterpriseApiRoutes.SecurityApp.EventProcedures.decoratedEventProcedure(eventProcedureId, eventId).url),
  );

export const createEventProcedure = async (
  payload: EventProceduresAPIType<EventProcedureStepAPIType[]> & EntityShare,
) => {
  const { share_request, ...rest } = payload;

  return glFetch('POST', qualifyUrl(EnterpriseApiRoutes.SecurityApp.EventProcedures.new().url), {
    entity: rest,
    share_request,
  });
};

export const updateEventProcedure = async ({
  eventProcedureId,
  eventProcedure,
}: {
  eventProcedureId: string;
  eventProcedure: EventProceduresAPIType<EventProcedureStepAPIType[]>;
}) =>
  glFetch(
    'PUT',
    qualifyUrl(EnterpriseApiRoutes.SecurityApp.EventProcedures.singleEventProcedure(eventProcedureId).url),
    eventProcedure,
  );

export const fetchEventProcedureSteps = async (
  page: number,
  perPage: number,
  query: string = null,
  orderBy: string = undefined,
  direction: string = undefined,
): Promise<EventProcedureStepsIndexAPIType> => {
  const params = [`page=${page}`, `per_page=${perPage}`];
  if (query) params.push(`query=${encodeURI(query)}`);

  if (orderBy) {
    params.push(`sort=${orderBy}`);
  }

  if (direction) {
    params.push(`direction=${direction}`);
  }

  return glFetch('GET', qualifyUrl(EnterpriseApiRoutes.SecurityApp.EventProcedures.steps.index(params.join('&')).url));
};

export const createEventProcedureStep = async (payload: EventProcedureStepAPIType & EntityShare) => {
  const { share_request, ...rest } = payload;

  return glFetch('POST', qualifyUrl(EnterpriseApiRoutes.SecurityApp.EventProcedures.steps.new().url), {
    entity: rest,
    share_request,
  });
};

export const updateEventProcedureStep = async ({ stepId, step }: { stepId: string; step: EventProcedureStepAPIType }) =>
  glFetch(
    'PUT',
    qualifyUrl(EnterpriseApiRoutes.SecurityApp.EventProcedures.steps.singleEventProcedureStep(stepId).url),
    step,
  );

export const deleteEventProcedure = async ({ id }: { id: string }) => {
  glFetch('DELETE', qualifyUrl(EnterpriseApiRoutes.SecurityApp.EventProcedures.singleEventProcedure(id).url));
};

export const fetchLinkedProcedures = async (stepId: string): Promise<string[]> =>
  glFetch('GET', qualifyUrl(EnterpriseApiRoutes.SecurityApp.EventProcedures.steps.linkedProcedures(stepId).url));

export const deleteEventProcedureStep = async ({ id }: { id: string }) => {
  glFetch('DELETE', qualifyUrl(EnterpriseApiRoutes.SecurityApp.EventProcedures.steps.singleEventProcedureStep(id).url));
};

export const fetchParametersForView = async (id: string, type: string): Promise<SingletonParameter[]> =>
  glFetch('GET', qualifyUrl(EnterpriseApiRoutes.SecurityApp.EventProcedures.steps.viewParameter(id, type).url));
